import {
  Component,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ChangeDetectorRef,
  ViewEncapsulation,
  TemplateRef
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { BehaviorSubject, skip, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment';

import {
  SystemService,
  WorkflowService,
  WorkflowContextService
} from 'src/app/services';

import { Client } from 'src/app/models/client';
import { FilterBuilderParam } from 'src/app/models/filter-builder';
import { ColumnOption } from 'src/app/models/grid-settings';

import {
  CommonTableComponent,
  TableColumn
} from 'src/app/components/common/common-table/common-table.component';
import { InspectionsActionMenuComponent } from '../inspections-action-menu/inspections-action-menu.component';
import { InspectionsJanusViewDetailsComponent } from '../inspections-janus-view-details/inspections-janus-view-details.component';
import {
  ItemSearchFilter,
  ItemSearchOptionField,
  ItemSearchOptionFieldOption,
  ItemSearchUtils
} from 'src/app/components/filter-list/models/filterClasses';
import { MapDataRequest } from 'src/app/components/arcgis/basic-esri-map/basic-esri-map.component';
import { MapExtent } from 'src/app/models/mapextent';
import { GeomUtils } from 'src/app/components/arcgis/geom-utils';
import { CommonPaginationData } from 'src/app/components/common/common-pagination/common-pagination.component.model';

import { JsonNetDecycle } from 'src/app/services/utilities/json-net-decycle';
import { InspectionsJanusService } from '../inspections-janus.service';
import { ComponentIds } from 'src/app/models/component-ids';
import { InspectionStep } from 'src/app/models/activities';

const statusOptions: any[] = [];

@Component({
  selector: 'app-inspections-queue',
  standalone: false,
  templateUrl: './inspections-queue-janus.component.html',
  styleUrls: ['./inspections-queue-janus.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class InspectionsQueueJanusComponent implements OnInit, OnDestroy {
  public id = ComponentIds.INSPECTION_QUEUE;
  @Input() public client!: Client;
  @Input() private staticFilters!: ItemSearchFilter;
  @Input() public sortField: string = 'inspectionAppNumber';
  @Input() public sortDescending: boolean = true;

  @Output() private updateStatus = new EventEmitter();
  @Output() private dataLoaded = new EventEmitter<string>();
  @Output() recordCountSet = new EventEmitter<number>();
  @Output() filtersChanged = new EventEmitter<boolean>();

  @ViewChild(CommonTableComponent) table!: CommonTableComponent;
  @ViewChild('inspectionNoTemplate', { static: true })
  inspectionNoTemplate!: TemplateRef<any>;

  data: any[] = [];
  filteredData: any[] = [];
  pagedDataForPager: any[] = [];
  columns: TableColumn[] = [];
  expandedComponent: any = null;
  expandedComponentInputs: any;

  viewMode: 'list' | 'map' = 'list';
  mapFilters$ = new BehaviorSubject<any>(null);
  private q?: MapDataRequest;
  private currentMapExtent: MapExtent | null = null;

  currentPage = 0;
  pageSize = 10;
  readonly pageSizeOptions: number[] = [10, 25, 100, 200];
  totalPages = 0;
  mapPopupActions = [
    {
      title: 'View Application',
      id: 'view-application',
      className: 'esri-icon-zoom-out-magnifying-glass',
      handler: (item: any) => {
        if (!item?.id) return;
        this._workflowSvc.getInspectionJanus(item.id).subscribe(
          (inspectionDetails: any) => {
            const appId = this.resolveApplicationId(inspectionDetails, item);
            if (appId) {
              this._router.navigate([
                `/application/workflow-application/${appId}`
              ]);
            } else {
              this.toast.error('Application ID not found.');
            }
          },
          error => {
            console.error(
              'Failed to fetch inspection details for navigation',
              error
            );
            this.toast.error('Failed to load application details.');
          }
        );
      }
    },
    GeomUtils.CopyCoordinatesToClipboardAction,
    GeomUtils.NavigateToGoogleAction,
    GeomUtils.NavigateToBingAction
  ];

  totalRecords = 0;
  baseTotalRecords = 0;
  isLoading = true;

  public filterOptions: ItemSearchOptionField[] = [];
  public filterCriteria: FilterBuilderParam[] = [];
  public columnOptions: ColumnOption[] = [];
  public exportColumns: string[] = [];

  private isDestroyed = false;
  private filterSub?: Subscription;

  private workflowApplicationId!: string;
  public activity: any;

  isEditorVisible: boolean = false;
  private isFetching = false;

  private readonly SORT_CACHE_KEY = 'datatable-inspections-queue-sort';
  private readonly UI_STATE_CACHE_KEY = 'inspections-queue-ui-state';
  private activeSearchKey = '';
  private lastCompletedSearchKey = '';
  private activeInlineRow: any = null;
  isSorting = false;

  private pendingDeepLinkRegistrationId: string | null = null;
  private pendingDeepLinkView: string | null = null;
  private deepLinkLookupId: string | null = null;
  // True while the table shows only the inspection from the URL instead of the paged list.
  private linkedOnly = false;
  private readonly DEEP_LINK_TABS = ['documents', 'schedule', 'complete', 'notes', 'LinktoApplication', 'view-summary'];

  private readonly PAGE_SIZE_STORAGE_KEY =
    'wm-common-table-page-size-insections-queue-janus';
  constructor(
    private systemSvc: SystemService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _workflowSvc: WorkflowService,
    private _context: WorkflowContextService,
    private ref: ChangeDetectorRef,
    private toast: ToastrService,
    private inspectionsJanusSvc: InspectionsJanusService
  ) {
    this.setupFilterCriteria();
  }

  private getValidId(...ids: any[]): string {
    for (const id of ids) {
      if (id === undefined || id === null) continue;
      const safeId = String(id).trim();
      if (safeId && safeId !== 'undefined' && safeId !== 'null') {
        return safeId;
      }
    }
    return '';
  }

  private resolveApplicationId(
    details?: any,
    fallbackRow?: any,
    activity?: any
  ): string {
    return this.getValidId(
      activity?.workflowApplicationId,
      activity?.WorkflowApplicationId,
      activity?.model?.inspection?.workflowApplicationId,
      activity?.model?.inspection?.inspectionApplicationId,
      activity?.model?.scheduleStepActivity?.workflowApplicationId,
      activity?.model?.completeStepActivity?.workflowApplicationId,
      details?.inspectionApplicationId,
      details?.InspectionApplicationId,
      details?.linkedApplicationId,
      details?.LinkedApplicationId,
      details?.applicationId,
      details?.ApplicationId,
      fallbackRow?.workflowApplicationId,
      fallbackRow?.inspectionApplicationId
    );
  }

  private setActivityApplicationId(activity: any, appId: string): any {
    if (!activity || !appId) return activity;
    activity.workflowApplicationId = activity.workflowApplicationId || appId;
    if (activity.model?.inspection) {
      activity.model.inspection.workflowApplicationId =
        activity.model.inspection.workflowApplicationId || appId;
      activity.model.inspection.inspectionApplicationId =
        activity.model.inspection.inspectionApplicationId || appId;
    }
    if (activity.model?.scheduleStepActivity) {
      activity.model.scheduleStepActivity.workflowApplicationId =
        activity.model.scheduleStepActivity.workflowApplicationId || appId;
    }
    if (activity.model?.completeStepActivity) {
      activity.model.completeStepActivity.workflowApplicationId =
        activity.model.completeStepActivity.workflowApplicationId || appId;
    }
    return activity;
  }

  setViewMode(mode: 'list' | 'map'): void {
    if (this.viewMode === mode) return;

    this.viewMode = mode;
    this.persistUiStateToCache();
    this.syncSharedStateToRoute();
    this.emitMapRefresh();
    if (mode === 'map' && !this.data.length) {
      this.fetchInspections(this.currentPage);
    }
  }

  onMapDataRequested(q: MapDataRequest): void {
    this.q = q;
    this.currentMapExtent = q.ext;
    this.loadMapInspections();
  }

  onSortChanged(event: any) {
    const sortFieldMap: { [key: string]: string } = {
      inspectionAppNumber: 'inspectionAppNumber',
      appNo: 'applicationNumber',
      appType: 'applicationType',
      inspector: 'inspectorName',
      status: 'inspectionStatus'
    };

    const apiSortField =
      sortFieldMap[event.sortField] || event.sortField;
    // End Code added by Sanchit on 09/11/2026
    if (
      this.sortField === apiSortField &&
      this.sortDescending === event.sortDescending
    ) {
      return;
    }

    this.sortField = apiSortField;
    this.sortDescending = event.sortDescending;

    this.isSorting = true;

    localStorage.setItem(
      this.SORT_CACHE_KEY,
      JSON.stringify({
        sortField: this.sortField,
        sortDescending: this.sortDescending
      })
    );
    this.syncSharedStateToRoute();

    const data = this.inspectionsJanusSvc.getData();

    this.inspectionsJanusSvc.setData({
      ...data,
      reRunInspectionsTable: true
    });
  }

  ngOnInit(): void {
    const storedPageSize = localStorage.getItem(this.PAGE_SIZE_STORAGE_KEY);

    if (storedPageSize) {
      const pageSize = Number(storedPageSize);

      if ([10, 25, 100, 200].includes(pageSize)) {
        this.pageSize = pageSize;
      }
    }

    const sortCache = localStorage.getItem(this.SORT_CACHE_KEY);

    if (sortCache) {
      try {
        const parsed = JSON.parse(sortCache);
        this.sortField = parsed.sortField ?? 'inspectionAppNumber';
        this.sortDescending = parsed.sortDescending ?? true;
      } catch (e) {
        console.error('Invalid sort cache', e);
      }
    }
    this.restoreUiStateFromCache();

    this._route.queryParamMap.subscribe(params => {
      const pageParam = params.get('p');
      const page = Number(pageParam ?? 1);
      const normalizedPage = Number.isNaN(page) || page < 1 ? 1 : page;
      this.currentPage = normalizedPage - 1;

      const id = params.get('id');
      const view = params.get('view');
      this.pendingDeepLinkRegistrationId = id;
      this.pendingDeepLinkView = view;

      if (!id || !view) {
        this.clearInlineState();
        if (this.linkedOnly) {
          this.linkedOnly = false;
          this.fetchInspections(this.currentPage, true);
        }
      } else if (!this.isFetching && this.data.length && this.activeInlineRow?.id !== id) {
        // Route reused (in-app link / back-forward): open without waiting for a reload.
        this.applyDeepLink(this.data);
      }

      const sortField = params.get('sf');
      const sortDesc = params.get('sd');
      if (sortField) this.sortField = sortField;
      if (sortDesc !== null) this.sortDescending = sortDesc === '1';

      const vm = params.get('vm') as 'list' | 'map';
      if (vm === 'list' || vm === 'map') {
        this.viewMode = vm;
      }
    });

    const data = this.inspectionsJanusSvc.getData();
    this.setupColumns();

    this.inspectionsJanusSvc.setData({
      ...data,
      columns: this.columns,
      getExportOptions: (useExcelFormat: boolean) =>
        this.getExportOptions(useExcelFormat)
    });

    this.linkedOnly = !!(this.pendingDeepLinkRegistrationId && this.pendingDeepLinkView);
    this.initializeData();
    this.filterSub = this.inspectionsJanusSvc.data$
      .pipe(skip(1))
      .subscribe(data => {
        if (!data.reRunInspectionsTable) {
          return;
        }

        this.exitLinkedOnly();

        this.inspectionsJanusSvc.setData({
          ...data,
          reRunInspectionsTable: false,
          columns: this.columns,
          getExportOptions: (useExcelFormat: boolean) =>
            this.getExportOptions(useExcelFormat)
        });
        this.fetchInspections(this.currentPage, true);
        this.emitMapRefresh();
      });
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.ref.detach();
    this.filterSub?.unsubscribe();
  }

  setupColumns() {
    this.columns = [
      {
        key: 'inspectionAppNumber',
        apiKey: 'inspectionAppNumber',
        label: 'Inspection #',
        alwaysVisible: true,
        headerType: 'text',
        cellType: 'template',
        cellTemplate: this.inspectionNoTemplate
      } as any,
      {
        key: 'appNo',
        apiKey: 'appNumber',
        label: 'App #',
        alwaysVisible: true,
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'applicantName',
        apiKey: 'applicantName',
        label: 'Applicant',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'appType',
        apiKey: 'workflowName',
        label: 'App Type',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'appCreatedOnDate',
        apiKey: 'appCreatedOn',
        label: 'App Date',
        headerType: 'text',
        cellType: 'text'
      } as any,

      {
        key: 'applicantPhone',
        apiKey: 'applicantPhone',
        label: 'Applicant Phone',
        headerType: 'text',
        cellType: 'text',
        visible: false,
        hidden: true
      } as any,
      {
        key: 'parcelId',
        apiKey: 'parcelId',
        label: 'Parcel ID',
        headerType: 'text',
        cellType: 'text',
        visible: false,
        hidden: true
      } as any,

      {
        key: 'inspectionCompletedOnDate',
        apiKey: 'inspectionCompletedOn',
        label: 'Inspection Completed Date',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'inspector',
        apiKey: 'inspectorId',
        label: 'Inspector',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'inspectionType',
        apiKey: 'inspectionType',
        label: 'Inspection Type',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'status',
        apiKey: 'inspectionStatus',
        label: 'Status',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'appDescription',
        apiKey: 'appDescription',
        label: 'Description',
        headerType: 'text',
        cellType: 'text',
        visible: false,
        hidden: true
      } as any,
      {
        key: 'requestedOnDate',
        apiKey: 'requestedOn',
        label: 'Requested Date',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'scheduledOnDate',
        apiKey: 'scheduledOn',
        label: 'Scheduled Date',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'scheduledOnTime',
        apiKey: 'scheduledOnTime',
        label: 'Scheduled Time',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'address',
        apiKey: 'address',
        label: 'Address',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'city',
        apiKey: 'city',
        label: 'City',
        headerType: 'text',
        cellType: 'text'
      } as any,

      {
        key: 'action',
        label: 'View',
        alwaysVisible: true,
        headerType: 'text',
        cellType: 'component',
        hideMenu: true,
        sortable: false,
        component: InspectionsActionMenuComponent,
        componentInputs: (row: any) => {
          return {
            row: row,
            viewDetails: (r: any) => {
              this.openInline(r, 'documents');
              this._router.navigate([], {
                relativeTo: this._route,
                queryParams: { p: this.currentPage + 1, view: 'documents', id: r.id },
                queryParamsHandling: 'merge'
              });
            }
          };
        }
      } as any
    ];
  }

  private initializeData() {
    this.fetchInspections(this.currentPage, true);
  }

  private getMergedFilters(): any[] {
    const serviceData = this.inspectionsJanusSvc.getData();

    // Group 1: Standard UI Filters that require ItemSearchUtils merging (Status, Dates, etc)
    const uiFilters = [
      ...(this.filterOptions || []),
      ...(serviceData.filterInputs || []),
      ...(serviceData.attributeFilterInputs || [])
    ].filter((f: any) => {
      const title = String(f?.title || f?.field || f?.name || '')
        .trim()
        .toLowerCase();
      return title !== 'inspectionmywork';
    });

    const staticFields = this.staticFilters
      ? this.staticFilters.options.map(f => f.field)
      : null;

    // Merge standard properties so they correctly map backend operators. Safely catch nulls.
    let merged =
      uiFilters.length > 0
        ? ItemSearchUtils.mergeFilters(uiFilters, staticFields)
        : staticFields || [];
    if (!Array.isArray(merged)) {
      merged = merged ? [merged] : [];
    }

    // Group 2: The dynamically structured JSON simple searches (Append AFTER merge to prevent mapping corruption)
    const searchTextFilters = serviceData.searchText || [];
    if (searchTextFilters.length > 0) {
      const explicitFields = uiFilters.map((f: any) =>
        String(f?.title || f?.field || f?.name || '')
          .trim()
          .toLowerCase()
      );

      const sanitizedSearchTextFilters = searchTextFilters.filter((f: any) => {
        const key = String(f?.title || f?.id || f?.field || f?.name || '')
          .trim()
          .toLowerCase();
        // Exclude if it's already explicitly filtered
        return !explicitFields.includes(key);
      });
      merged.push(...sanitizedSearchTextFilters);
    }

    return merged;
  }

  private syncSharedStateToRoute(): void {
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: {
        p: this.currentPage + 1,
        sf: this.sortField,
        sd: this.sortDescending ? '1' : '0',
        vm: this.viewMode
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  fetchInspections(page: number, force: boolean = false): void {
    if (this.linkedOnly && this.pendingDeepLinkRegistrationId) {
      this.loadLinkedInspection();
      return;
    }

    this.currentPage = page;
    const mergedFilters = this.getMergedFilters();

    const requestPage = this.currentPage;
    const requestSize = this.pageSize;
    const cacheKey = this.buildSearchCriteriaKey(mergedFilters, requestPage);
    const searchKey = this.buildSearchKey(
      mergedFilters,
      requestPage,
      requestSize
    );

    if (this.isFetching && this.activeSearchKey === searchKey) return;
    if (!force && this.restoreCachedResult(cacheKey)) return;
    if (
      !force &&
      !this.isSorting &&
      this.lastCompletedSearchKey === searchKey &&
      this.data.length
    )
      return;

    this.isFetching = true;
    this.activeSearchKey = searchKey;

    if (!this.isSorting) this.isLoading = true;
    this.detectChanges();

    this._workflowSvc
      .searchInspectionsJanus(
        this.sortField,
        this.sortDescending,
        mergedFilters,
        requestPage,
        requestSize,
        null as any
      )
      .subscribe({
        next: (result: any) => {
          this.baseTotalRecords = result?.recordCount ?? 0;
          this.totalRecords = this.baseTotalRecords;
          this.totalPages = Math.ceil(this.totalRecords / this.pageSize);

          const mappedRows = (result?.inspections || []).map((rawInsp: any) =>
            this.normalizeInspection(rawInsp)
          );

          this.data = mappedRows;
          this.filteredData = [...mappedRows];
          this.pagedDataForPager = [...mappedRows];

          if (this.pendingDeepLinkRegistrationId && this.pendingDeepLinkView) {
            const row = mappedRows.find((r: any) => r.id === this.pendingDeepLinkRegistrationId);
            if (row) {
              setTimeout(() => this.openInline(row, this.getDeepLinkTab()));
            } else {
              this.closeInlineRoute();
            }
          } else if (this.activeInlineRow && this.table) {
            const currentId = this.activeInlineRow.id;
            const updatedRow = mappedRows.find((r: any) => r.id === currentId);
            if (updatedRow) {
              this.activeInlineRow = updatedRow;
              setTimeout(() => {
                if (this.table) {
                  this.table.expandRow(updatedRow);
                }
              });
            } else {
              this.clearInlineState();
            }
          }

          for (const ac of this.getExportableColumns()) {
            if (
              !this.columnOptions.find(co => co.name === (ac.apiKey as string))
            ) {
              const newOption = new ColumnOption({
                label: ac.label ? ac.label : ac.apiKey,
                name: ac.apiKey as string,
                includeInExport: true
              });
              this.columnOptions.push(newOption);
            }
          }
          this.exportColumns = this.columnOptions
            .filter(co => co.includeInExport)
            .map(co => co.name);

          const currentData = this.inspectionsJanusSvc.getData();
          if (
            JSON.stringify(currentData.columnOptions) !==
              JSON.stringify(this.columnOptions) ||
            JSON.stringify(currentData.exportOptions) !==
              JSON.stringify(this.exportColumns)
          ) {
            this.inspectionsJanusSvc.setData({
              ...currentData,
              columnOptions: this.columnOptions,
              exportOptions: this.exportColumns,
              getExportOptions: (useExcelFormat: boolean) =>
                this.getExportOptions(useExcelFormat)
            });
          } else if (!currentData.getExportOptions) {
            this.inspectionsJanusSvc.setData({
              ...currentData,
              getExportOptions: (useExcelFormat: boolean) =>
                this.getExportOptions(useExcelFormat)
            });
          }

          this.recordCountSet.emit(this.totalRecords);
          this.dataLoaded.emit('InspectionQueueComponent');
          this.cacheCurrentResult(cacheKey);

          if (this.q) {
            this.emitMapDataFromCurrentRows(this.q);
            this.q = undefined;
          }

          this.isLoading = false;
          this.isSorting = false;
          this.isFetching = false;
          this.lastCompletedSearchKey = this.activeSearchKey;
          this.activeSearchKey = '';
          this.detectChanges();
        },
        error: err => {
          console.error(
            '[Inspections Queue Janus] Failed to load inspections',
            err
          );
          this.data = [];
          this.filteredData = [];
          this.pagedDataForPager = [];
          this.totalRecords = 0;
          this.totalPages = 0;
          this.currentPage = 0;
          this.baseTotalRecords = 0;
          this.isLoading = false;
          this.isSorting = false;
          this.isFetching = false;
          this.activeSearchKey = '';
          if (this.q) {
            this.emitMapDataFromCurrentRows(this.q);
            this.q = undefined;
          }
          this.detectChanges();
        }
      });
  }

  private loadMapInspections(): void {
    if (!this.q) return;

    const mergedFilters = this.getMergedFilters();
    const mapExtent = this.q.ext;
    const request = this.q;
    const initialSize =
      this.baseTotalRecords > 0
        ? Math.max(this.baseTotalRecords, this.pageSize)
        : this.pageSize;

    const runMapSearch = (
      requestSize: number,
      allowRetryWithRecordCount: boolean
    ): void => {
      this._workflowSvc
        .searchInspectionsJanus(
          this.sortField,
          this.sortDescending,
          mergedFilters,
          0,
          requestSize,
          mapExtent as any
        )
        .subscribe({
          next: (result: any) => {
            const recordCount = result?.recordCount ?? 0;
            const resultRowsCount = Array.isArray(result?.inspections)
              ? result.inspections.length
              : 0;

            if (
              allowRetryWithRecordCount &&
              recordCount > resultRowsCount &&
              requestSize < recordCount
            ) {
              runMapSearch(recordCount, false);
              return;
            }

            let rows = (result?.inspections || []).map((rawInsp: any) =>
              this.normalizeInspection(rawInsp)
            );

            let mapData = rows
              .filter(
                (row: any) =>
                  Array.isArray(row.geometryWkts) && row.geometryWkts.length > 0
              )
              .map((row: any) => this.toMapData(row));

            if (mapData.length === 0) {
              mapData = (this.pagedDataForPager || [])
                .filter(
                  row =>
                    Array.isArray(row.geometryWkts) &&
                    row.geometryWkts.length > 0
                )
                .map(row => this.toMapData(row));
            }

            request.gimmeTheData$.emit(mapData);
            if (this.q === request) {
              this.q = undefined;
            }
          },
          error: (err: any) => {
            console.error(
              '[Inspections Queue Janus] Failed to load map inspections',
              err
            );
            this.emitMapDataFromCurrentRows(request);
            if (this.q === request) {
              this.q = undefined;
            }
          }
        });
    };

    runMapSearch(initialSize, true);
  }

  private exitLinkedOnly(): void {
    if (!this.linkedOnly) return;
    this.linkedOnly = false;
    this.closeInlineRoute();
  }

  private getDeepLinkTab(): string {
    // Older links used view=details, which is not a tab.
    const view = this.pendingDeepLinkView || '';
    return this.DEEP_LINK_TABS.includes(view) ? view : 'documents';
  }

  private applyDeepLink(rows: any[]): void {
    const row = rows.find((r: any) => r.id === this.pendingDeepLinkRegistrationId);
    if (row) {
      setTimeout(() => this.openInline(row, this.getDeepLinkTab()));
    } else {
      this.loadLinkedInspection();
    }
  }

  private loadLinkedInspection(): void {
    const id = this.pendingDeepLinkRegistrationId;
    if (!id || !this.pendingDeepLinkView || this.deepLinkLookupId === id) return;

    this.linkedOnly = true;
    this.deepLinkLookupId = id;
    this.isLoading = true;
    this.detectChanges();

    // Look the inspection up by id so the user's saved filters, sort and page don't hide it.
    const idFilter = new ItemSearchOptionField({
      title: 'inspectionId',
      options: [
        new ItemSearchOptionFieldOption({
          title: '',
          selected: true,
          filterText: '',
          searchText: id,
          strOperator: 'is'
        })
      ]
    });

    this._workflowSvc
      .searchInspectionsJanus(this.sortField, this.sortDescending, [idFilter], 0, 1, null as any)
      .subscribe({
        next: (result: any) => {
          this.deepLinkLookupId = null;
          if (this.pendingDeepLinkRegistrationId !== id) return;

          this.isLoading = false;
          const raw = (result?.inspections || []).find((r: any) => r?.inspectionId === id);
          if (!raw) {
            this.toast.warning('The requested inspection was not found or is not available in this department.');
            // Removing id from the URL drops linkedOnly and loads the normal list.
            this.closeInlineRoute();
            return;
          }

          const row = this.normalizeInspection(raw);
          this.data = [row];
          this.filteredData = [row];
          this.pagedDataForPager = [row];
          this.currentPage = 0;
          this.totalRecords = 1;
          this.baseTotalRecords = 1;
          this.totalPages = 1;
          this.recordCountSet.emit(this.totalRecords);
          this.dataLoaded.emit('InspectionQueueComponent');
          this.detectChanges();

          if (this.activeInlineRow?.id === id && this.expandedComponent) {
            this.activeInlineRow = row;
            setTimeout(() => this.table?.expandRow(row));
          } else {
            setTimeout(() => this.openInline(row, this.getDeepLinkTab()));
          }
        },
        error: (err: any) => {
          this.deepLinkLookupId = null;
          this.isLoading = false;
          console.error('[Inspections Queue Janus] Failed to load linked inspection', err);
          this.toast.error('Failed to load the requested inspection.');
          this.closeInlineRoute();
        }
      });
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.syncSharedStateToRoute();
    this.fetchInspections(page);
  }

  onRowsPerPageChange(size: number): void {
    const pageSize = Number(size) || 10;
    localStorage.setItem(this.PAGE_SIZE_STORAGE_KEY, String(pageSize));
    this.pageSize = pageSize;
    this.currentPage = 0;
    this.syncSharedStateToRoute();
    this.fetchInspections(0);
    this.emitMapRefresh();
  }

  goToFirstPage(): void {
    this.onPageChange(0);
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.onPageChange(this.currentPage - 1);
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.onPageChange(this.currentPage + 1);
    }
  }

  goToLastPage(): void {
    if (this.totalPages > 0) {
      this.onPageChange(this.totalPages - 1);
    }
  }

  get paginationData(): CommonPaginationData {
    return {
      variant: 'staff',
      totalPages: this.totalPages,
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      showRowsPerPage: true,
      showTotalRecords: true,
      visiblePages: this.getVisiblePages(this.totalPages, this.currentPage),
      canGoPrevious: this.currentPage > 0,
      totalRecords: this.totalRecords,
      canGoNext: this.totalPages > 0 && this.currentPage < this.totalPages - 1
    };
  }

  private getVisiblePages(
    totalPages: number,
    currentPage: number
  ): (number | 'dots')[] {
    const maxVisible = 5;
    if (!totalPages || totalPages <= 1) return [];

    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }

    const pages: (number | 'dots')[] = [];
    const start = Math.max(0, currentPage - 2);
    const end = Math.min(totalPages - 1, currentPage + 2);

    if (start > 0) {
      pages.push(0);
      if (start > 1) pages.push('dots');
    }

    for (let i = start; i <= end; i++) pages.push(i);

    if (end < totalPages - 1) {
      if (end < totalPages - 2) pages.push('dots');
      pages.push(totalPages - 1);
    }

    return pages;
  }

  public processInspectionActivity(
    workflowAppId: string,
    inspId: string,
    actionType: string,
    tabName: string,
    row: any
  ) {
    const appId = this.resolveApplicationId(null, row, {
      workflowApplicationId: workflowAppId
    });
    if (!appId) {
      this.toast.error('Invalid Application ID. Cannot process activity.');
      return;
    }

    this.workflowApplicationId = appId;
    this._workflowSvc
      .getInspectionActivity(appId, inspId, actionType)
      .subscribe({
        next: activityResp => {
          this.isLoading = false;
          if (activityResp) {
            this.activity = this.setActivityApplicationId(activityResp, appId);
            this.openInline(row, tabName);
          } else {
            this.toast.info(
              'The action you requested is not available for this inspection.',
              'Action unavailable.'
            );
          }
        },
        error: err => {
          console.error('Error fetching activity details:', err);
          this.isLoading = false;
          this.toast.error(
            'A network error occurred while fetching activity details.'
          );
        }
      });
  }

  onInspectionNumberClick(event: MouseEvent, row: any) {
    event.stopPropagation();
    this.openInline(row, 'documents');
  }

  openInline(row: any, tabName: string) {
    if (!this.table) return;

    this.activeInlineRow = row;
    this.table.expandRow(row);
    this.expandedComponent = InspectionsJanusViewDetailsComponent;

    if (row && row.workflowApplicationId) {
      this.workflowApplicationId = row.workflowApplicationId;
    }

    this.expandedComponentInputs = (tableRow: any) => ({
      row: tableRow,
      initialTab: tabName,
      activity: this.activity,

      fetchActivity: (actionType: string) => {
        this._workflowSvc.getInspectionJanus(tableRow.id).subscribe(
          (inspectionDetails: any) => {
            const appId = this.resolveApplicationId(
              inspectionDetails,
              tableRow
            );
            if (!appId) {
              this.toast.error('Application UUID not found.');
              return;
            }
            this.workflowApplicationId = appId;

            this._workflowSvc
              .getInspectionActivity(appId, tableRow.id, actionType)
              .subscribe({
                next: (res: any) => {
                  if (res) {
                    this.activity = this.setActivityApplicationId(res, appId);
                    this.openInline(
                      tableRow,
                      actionType === 'scheduleInspection'
                        ? 'schedule'
                        : 'complete'
                    );
                  } else {
                    this.toast.info(
                      'The action you requested is not available for this inspection.',
                      'Action unavailable.'
                    );
                  }
                },
                error: (err: any) => {
                  console.error('Failed to load activity details', err);
                  this.toast.error('Failed to load activity details.');
                }
              });
          },
          (error: any) => {
            console.error('Failed to fetch inspection details', error);
            this.toast.error('Failed to load inspection details.');
          }
        );
      },
      onScheduleSubmit: (scheduleData: any) => {
        this.setInlineView(tableRow, 'schedule');
        this.submitActivity(scheduleData, 'Inspection Scheduled Successfully');
      },
      onCompleteSubmit: (completeData: any) => {
        this.setInlineView(tableRow, 'complete');
        this.submitActivity(completeData, 'Inspection Completed Successfully');
      },
      closeExpansion: () => {
        this.closeInlineRoute();
      }
    });
  }

  // The refresh after submit reopens the panel on the URL's view, which may still be View Summary.
  private setInlineView(row: any, view: string): void {
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { view, id: row?.id },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  submitActivity(activityData: any, successMessage: string) {
    const appId = this.resolveApplicationId(
      null,
      this.activeInlineRow,
      activityData
    );
    if (!appId) {
      this.toast.error('Application ID is missing, cannot submit activity.');
      return;
    }
    // Removed this.closeInlineRoute(); to allow proceeding to the next step
    this.workflowApplicationId = appId;

    const activityToSubmit = this.setActivityApplicationId(activityData, appId);
    const decActivity = JsonNetDecycle.decycle(activityToSubmit);

    // Call saveWorkflowActivity for Schedule step, submitWorkflowActivity for Complete step
    const isConsolidatedSchedule =
      activityData.type === 'consolidated-inspection-activity' &&
      activityData.submittedStep === InspectionStep.Schedule;
    const request$: any = isConsolidatedSchedule
      ? this._workflowSvc.saveWorkflowActivity(appId, decActivity)
      : this._workflowSvc.submitWorkflowActivity(appId, decActivity, false);

    request$.subscribe({
      next: (res: any) => {
        if (res && res.saveSuccess === false) {
          this.toast.error(res.message || 'Failed to update inspection.');
          this.fetchInspections(this.currentPage, true);
          return;
        }
        if (
          res &&
          !isConsolidatedSchedule &&
          res.activityWasCompleted === false
        ) {
          this.toast.error(res.message || 'Failed to update inspection.');
          this.fetchInspections(this.currentPage, true);
          return;
        }
        this.toast.success(successMessage);

        // Increase delay to allow backend CQRS read-models to fully update
        setTimeout(() => {
          this.fetchInspections(this.currentPage, true);
          this.updateStatus.emit();
        }, 1500);
      },
      error: err => {
        console.error('Submit Activity Error:', err);
        this.toast.error('A network error occurred while submitting.');
        this.fetchInspections(this.currentPage, true);
      }
    });
  }

  public getExportOptions(useExcelFormat: boolean): any {
    const serviceData = this.inspectionsJanusSvc.getData();

    let rawFilters = [
      ...(this.filterOptions || []),
      ...(serviceData?.filterInputs ?? []),
      ...(serviceData?.attributeFilterInputs ?? [])
    ];

    rawFilters = rawFilters.filter((f: any) => {
      const key = String(f?.title || f?.field || f?.name || '')
        .trim()
        .toLowerCase();
      return key !== 'inspectionmywork';
    });

    const searchTextFilters = serviceData?.searchText || [];
    if (searchTextFilters.length > 0) {
      rawFilters.push(...searchTextFilters);
    }

    const mapExtent = this.viewMode === 'map' ? this.currentMapExtent : null;
    const exportColumns = this.exportColumns.length
      ? this.exportColumns
      : this.getExportableColumns().map(
          column => (column as any).apiKey as string
        );

    return {
      clientId: this.client?.id || this._context.client?.id || null,
      filters: rawFilters,
      additionalFilters: rawFilters,
      useExcelFormat: useExcelFormat,
      exportColumns: exportColumns,
      extent: mapExtent,
      sortField: this.sortField,
      sortDescending: this.sortDescending
    };
  }

  private safeFormatDate(dateStr: any, format: string): string {
    if (!dateStr) return '';
    const str = String(dateStr);

    // Ignore .NET default minimum dates
    if (
      str.includes('0001-01-01') ||
      str.includes('1970-01-01') ||
      str.includes('1969-12-31')
    )
      return '';

    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '';
      if (d.getFullYear() <= 1) return ''; // Catch edge-case uninitialized dates
      return moment(d).format(format);
    } catch (e) {
      return '';
    }
  }

  private detectChanges() {
    if (!this.isDestroyed) this.ref.detectChanges();
  }

  private buildSearchKey(
    filters: any[],
    requestPage: number,
    requestSize: number
  ): string {
    return JSON.stringify({
      sortField: this.sortField,
      sortDescending: this.sortDescending,
      filters: filters || [],
      requestPage,
      requestSize
    });
  }

  private buildSearchCriteriaKey(filters: any[], requestPage: number): string {
    return JSON.stringify({
      sortField: this.sortField,
      sortDescending: this.sortDescending,
      filters: filters || [],
      requestPage,
      pageSize: this.pageSize
    });
  }

  private cacheCurrentResult(cacheKey: string): void {
    const currentData = this.inspectionsJanusSvc.getData();
    this.inspectionsJanusSvc.setData({
      ...currentData,
      inspectionQueueCache: {
        cacheKey,
        data: [...this.data],
        filteredData: [...this.filteredData],
        pagedDataForPager: [...this.pagedDataForPager],
        totalRecords: this.totalRecords,
        totalPages: this.totalPages,
        baseTotalRecords: this.baseTotalRecords,
        currentPage: this.currentPage,
        pageSize: this.pageSize
      }
    });
  }

  private restoreCachedResult(cacheKey: string): boolean {
    const cache = this.inspectionsJanusSvc.getData().inspectionQueueCache;
    if (!cache || cache.cacheKey !== cacheKey) {
      return false;
    }

    this.data = [...(cache.data || [])];
    this.filteredData = [...(cache.filteredData || [])];
    this.pagedDataForPager = [...(cache.pagedDataForPager || [])];
    this.totalRecords = cache.totalRecords || 0;
    this.totalPages = cache.totalPages || 0;
    this.baseTotalRecords = cache.baseTotalRecords || 0;
    this.currentPage = cache.currentPage || 0;
    this.pageSize = cache.pageSize || this.pageSize;
    this.isLoading = false;
    this.isSorting = false;
    this.recordCountSet.emit(this.totalRecords);
    this.dataLoaded.emit('InspectionQueueComponent');
    this.detectChanges();
    return true;
  }

  private normalizeInspection(rawInsp: any): any {
    const statusNormalized = String(rawInsp.inspectionStatus || '')
      .trim()
      .toLowerCase()
      .replace(/\s/g, '');
    const isPending = [
      'pendingschedule',
      'pendinginspection',
      'requested',
      'scheduled',
      'needed'
    ].includes(statusNormalized);

    return {
      id: rawInsp.inspectionId,
      inspectionAppNumber: rawInsp.inspectionApplicationNumber || '',
      appNo: rawInsp.applicationNumber || '',
      applicantName: rawInsp.applicantName || '',
      appType: rawInsp.applicationType || '',
      appCreatedOnDate: this.safeFormatDate(
        rawInsp.applicationCreatedOnDate,
        'MM/DD/YYYY'
      ),
      applicantPhone: rawInsp.applicantPhoneNumber || '',
      parcelId: rawInsp.parcelID || '',
      // Only show completed date if the status is not pending
      inspectionCompletedOnDate: !isPending
        ? this.safeFormatDate(rawInsp.inspectionCompletedOnDate, 'MM/DD/YYYY')
        : '',
      inspector: rawInsp.inspectorName || '',
      inspectionType: rawInsp.inspectionType || '',
      status: rawInsp.inspectionStatus || '',
      appDescription: rawInsp.applicationDescription || '',
      requestedOnDate:
        rawInsp.requestedOnDate ||
        rawInsp.RequestedOnDate ||
        this.safeFormatDate(
          rawInsp.requestedOn || rawInsp.RequestedOn,
          'MM/DD/YYYY'
        ) ||
        '',
      scheduledOnDate:
        rawInsp.scheduledOnDate ||
        rawInsp.ScheduledOnDate ||
        this.safeFormatDate(
          rawInsp.scheduledOn || rawInsp.ScheduledOn,
          'MM/DD/YYYY'
        ) ||
        '',
      scheduledOnTime: rawInsp.scheduledOnTime || rawInsp.ScheduledOnTime || '',
      address: rawInsp.address || rawInsp.Address || '',
      city: rawInsp.city || rawInsp.City || '',
      geometryWkts: this.normalizeGeometryWkts(rawInsp.geometryWkts)
    };
  }

  private toMapData(row: any): any {
    const geoPt: any = row.geometryWkts?.length
      ? GeomUtils.WktToArcGisGeom(row.geometryWkts[0])
      : null;

    return {
      geometryWkts: row.geometryWkts,
      data: {
        id: row.id,
        appNo: row.appNo,
        permitNumber: row.appNo,
        inspectionAppNumber: row.inspectionAppNumber,
        inspectionType: row.inspectionType,
        status: row.status,
        inspector: row.inspector,
        appDate: row.appCreatedOnDate,
        inspectionCompleted: row.inspectionCompletedOnDate,
        inspectionDate: row.appCreatedOnDate,
        inspectionTime: '',
        lat: geoPt ? geoPt.y : null,
        lon: geoPt ? geoPt.x : null
      }
    };
  }

  private buildMapDataFromRows(rows: any[]): any[] {
    return (rows || [])
      .filter(
        row => Array.isArray(row.geometryWkts) && row.geometryWkts.length > 0
      )
      .map(row => this.toMapData(row));
  }

  private normalizeGeometryWkts(geometryWkts: any): string[] {
    if (Array.isArray(geometryWkts)) {
      return geometryWkts.filter(wkt => !!String(wkt || '').trim());
    }

    const singleWkt = String(geometryWkts || '').trim();
    return singleWkt ? [singleWkt] : [];
  }

  private getExportableColumns(): TableColumn[] {
    return this.columns.filter(
      column => !!String((column as any).apiKey || '').trim()
    );
  }

  private emitMapDataFromCurrentRows(request: MapDataRequest): void {
    let mapData = this.buildMapDataFromRows(this.data);
    if (mapData.length === 0) {
      mapData = this.buildMapDataFromRows(this.pagedDataForPager || []);
    }
    request.gimmeTheData$.emit(mapData);
  }

  private emitMapRefresh(): void {
    this.mapFilters$.next({ timestamp: Date.now() });
  }

  private restoreUiStateFromCache(): void {
    const cache = localStorage.getItem(this.UI_STATE_CACHE_KEY);
    if (!cache) return;

    try {
      const parsed = JSON.parse(cache) || {};
      const mode = String(parsed?.viewMode || '').toLowerCase();
      this.viewMode = mode === 'map' ? 'map' : 'list';
    } catch {
      localStorage.removeItem(this.UI_STATE_CACHE_KEY);
    }
  }

  private persistUiStateToCache(): void {
    const payload = JSON.stringify({ viewMode: this.viewMode });
    if (localStorage.getItem(this.UI_STATE_CACHE_KEY) === payload) return;

    localStorage.setItem(this.UI_STATE_CACHE_KEY, payload);
  }

  private setupFilterCriteria() {
    this.filterCriteria = [
      { id: 'inspectionAppNumber', name: 'Inspection #' },
      { id: 'appNumber', name: 'App #' },
      { id: 'applicantName', name: 'Applicant' },
      { id: 'workflowName', name: 'App Type' },
      {
        id: 'appCreatedOn',
        name: 'App Date',
        inputType: 'date',
        types: ['is', 'range']
      },
      { id: 'applicantPhone', name: 'Applicant Phone' },
      { id: 'parcelId', name: 'Parcel ID' },
      {
        id: 'inspectionCompletedOn',
        name: 'Inspection Complete Date',
        inputType: 'date',
        types: ['is', 'range']
      },
      {
        id: 'inspectorId',
        name: 'Inspector',
        types: ['is', 'in'],
        options: [{ name: 'None', value: 'NULL' }]
      },
      { id: 'inspectionType', name: 'Inspection Type' },
      {
        id: 'inspectionStatus',
        name: 'Status',
        types: ['is', 'in'],
        options: statusOptions
      },
      { id: 'appDescription', name: 'Description' },
      {
        id: 'requestedOn',
        name: 'Requested Date',
        inputType: 'date',
        types: ['is', 'range']
      },
      {
        id: 'scheduledOn',
        name: 'Scheduled Date',
        inputType: 'date',
        types: ['is', 'range']
      },
      { id: 'address', name: 'Address' },
      { id: 'city', name: 'City' }
    ];
  }

  private closeInlineRoute(): void {
    this.clearInlineState();
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { view: null, id: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private clearInlineState(): void {
    this.expandedComponent = null;
    this.expandedComponentInputs = null;
    this.activeInlineRow = null;
    if (this.table) {
      this.table.expandedRow = null;
    }
  }
}
